import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserService } from './user.services';

/** Emitted to queued 401 waiters when refresh fails so they do not hang. */
const refreshFailedSentinel = '__portal_auth_refresh_failed__';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  /** Bypasses interceptors to avoid circular HttpClient → AuthInterceptor dependency on refresh. */
  private readonly httpRaw: HttpClient;

  constructor(
    httpBackend: HttpBackend,
    private userService: UserService
  ) {
    this.httpRaw = new HttpClient(httpBackend);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip auth for login/refresh/otp
    if (
      req.url.includes('Login') ||
      req.url.includes('RefreshToken') ||
      req.url.includes('ValidateCredentials') ||
      req.url.includes('otp') ||
      req.url.includes('register/investor') ||
      req.url.includes('ForgetPassword') ||
      req.url.includes('ResetPassword')
    ) {
      return next.handle(req);
    }

    const token = this.userService.currentUserCredantials?.token;

    let authReq = req;
    if (token) {
      authReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`)
      });
    }

    return next.handle(authReq).pipe(
      catchError((error) => {
        // Handle unauthorized → try refresh
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(authReq, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.userService.isLoggedIn()) {
      this.userService.logout();
      return throwError(() => new Error('Not logged in'));
    }

    const creds = this.userService.currentUserCredantials;
    if (!creds?.refreshToken) {
      this.userService.logout();
      return throwError(() => new Error('No refresh token'));
    }

    // If refresh in progress → wait
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          if (token === refreshFailedSentinel) {
            return throwError(
              () => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' })
            );
          }
          const newToken = this.userService.currentUserCredantials?.token ?? token;
          const cloned = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${newToken}`)
          });
          return next.handle(cloned);
        })
      );
    }

    // Start refresh process
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const url = `${environment.appConfig.gatewayServiceBaseUrl}/api/portal/PortalAuthentication/RefreshToken`;
    const acceptLanguage =
      (localStorage.getItem('currentLang') || 'ar') === 'ar' ? 'ar-SA' : 'en-US';

    return this.httpRaw
      .post(url, {
        phoneNumber: creds.phoneNumber!,
        refreshToken: creds.refreshToken!,
      }, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Accept-Language': acceptLanguage,
        },
      })
      .pipe(
        switchMap((res: any) => {
          if (!res?.data) {
            return throwError(() => new Error('No token in refresh response'));
          }
          this.userService.currentUserCredantials = res.data;
          this.refreshTokenSubject.next(res.data.token);
          const cloned = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${res.data.token}`)
          });
          return next.handle(cloned);
        }),
        catchError((err) => {
          this.refreshTokenSubject.next(refreshFailedSentinel);
          this.userService.logout();
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
  }
}
